// src/components/MetaConnectCard.js — the whole Facebook Lead Ads integration
// as one button.
//
// Replaces the old form where a client had to find a Verify Token, an App
// Secret and a Page Access Token in Meta's developer console. Now: click
// Connect, approve in a Facebook popup, tick the pages you want. Every
// credential is handled server-side against one platform-owned Meta app and is
// never shown to, typed by, or sent to the browser.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getMetaStatus, metaConnectStart, getMetaConnectState,
  getMetaPages, saveMetaPages, disconnectMetaPage,
} from '../utils/api';
import { Modal } from './UI';

const POLL_MS = 1500;

function relTime(str) {
  if (!str) return null;
  const then = new Date(str.replace(' ', 'T'));
  if (isNaN(then)) return null;
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

export default function MetaConnectCard({ canManage, toast }) {
  const [status, setStatus]   = useState(null);   // null until first load
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [unavailable, setUnavailable] = useState('');

  // Page-picker modal, shown once the Facebook popup reports back
  const [picker, setPicker]     = useState(null); // { state, pages }
  const [selected, setSelected] = useState({});
  const [saving, setSaving]     = useState(false);

  // Held across the popup's lifetime so the message listener and the poller
  // both know which authorization they are waiting on.
  const pending = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await getMetaStatus();
      setStatus(r.data);
      setUnavailable('');
    } catch (e) {
      // 503 = server-side setup not done (migration or .env). That's an
      // operator problem, not something the client did wrong, so it gets a
      // plain explanation instead of a red error toast.
      if (e.response?.status === 503) setUnavailable(e.response.data?.error || 'Not available yet.');
      else setUnavailable('Could not load Facebook connection status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Once the popup finishes, fetch the pages that authorization can manage.
  const openPicker = useCallback(async (state) => {
    setBusy(true);
    try {
      const r = await getMetaPages(state);
      const pages = r.data.pages || [];
      if (!pages.length) {
        toast('No Facebook pages found on that account. Make sure you are an admin of the page.');
        return;
      }
      // Pre-tick what is already connected, so saving is additive rather than a
      // surprise reset for someone reconnecting to add one more page.
      const pre = {};
      pages.forEach(p => { if (p.already_connected) pre[p.id] = true; });
      setSelected(pre);
      setPicker({ state, pages });
    } catch (e) {
      toast(e.response?.data?.error || 'Could not load your Facebook pages.');
    } finally {
      setBusy(false);
    }
  }, [toast]);

  // The popup runs on a different host, so it reports back by postMessage.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.data?.source !== 'ems-meta-connect' || !pending.current) return;
      const state = pending.current;
      pending.current = null;
      // On success openPicker owns `busy` until the page list arrives, so the
      // button stays disabled through the whole flow rather than flickering
      // back to enabled while pages load.
      if (e.data.ok) openPicker(state);
      else { toast(e.data.error || 'Facebook connection was cancelled.'); setBusy(false); }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [openPicker, toast]);

  const connect = async () => {
    setBusy(true);
    let state, authUrl;
    try {
      const r = await metaConnectStart();
      state = r.data.state;
      authUrl = r.data.auth_url;
    } catch (e) {
      setBusy(false);
      toast(e.response?.data?.error || 'Could not start the Facebook connection.');
      return;
    }

    const popup = window.open(authUrl, 'ems_meta_connect', 'width=600,height=760,menubar=no,toolbar=no');
    if (!popup) {
      setBusy(false);
      toast('Please allow popups for this site, then click Connect again.');
      return;
    }
    pending.current = state;

    // Poll as well as listen: a popup blocker, a cross-origin-opener policy, or
    // the user closing the window mid-flow can all swallow the postMessage.
    // Whichever signal arrives first wins; `pending` makes it idempotent.
    const started = Date.now();
    const timer = setInterval(async () => {
      if (!pending.current) { clearInterval(timer); return; }
      // Client-side cap, deliberately shorter than the server's 15-minute state
      // TTL: someone who wandered off mid-login gets the button back rather
      // than a spinner that looks stuck.
      if (Date.now() - started > 5 * 60 * 1000) {
        clearInterval(timer); pending.current = null; setBusy(false);
        toast('The Facebook connection timed out. Please try again.');
        return;
      }
      try {
        const r = await getMetaConnectState(state);
        if (r.data.status === 'authorized') {
          clearInterval(timer); pending.current = null;
          try { popup.close(); } catch { /* cross-origin popup, already closing itself */ }
          openPicker(state);
        } else if (r.data.status === 'failed') {
          clearInterval(timer); pending.current = null; setBusy(false);
          toast(r.data.error || 'Facebook connection was cancelled.');
        }
      } catch {
        // 410 once the state has expired or been consumed — stop quietly.
        clearInterval(timer); pending.current = null; setBusy(false);
      }
    }, POLL_MS);
  };

  const savePages = async () => {
    const ids = Object.keys(selected).filter(id => selected[id]);
    if (!ids.length) { toast('Select at least one page.'); return; }
    setSaving(true);
    try {
      const r = await saveMetaPages(picker.state, ids);
      const { connected = [], failed = [] } = r.data;
      setPicker(null);
      await load();

      if (failed.length) {
        toast(`${connected.length} connected, ${failed.length} failed — ${failed[0].error}`);
      } else {
        const noLeadAccess = connected.filter(p => !p.can_manage_leads);
        toast(noLeadAccess.length
          ? `Connected, but you may not have lead access on ${noLeadAccess[0].name}. Ask a page admin to grant it.`
          : `Connected ${connected.length} page${connected.length === 1 ? '' : 's'}. New leads will arrive automatically.`);
      }
    } catch (e) {
      toast(e.response?.data?.error || 'Could not save your page selection.');
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async (page) => {
    if (!window.confirm(`Disconnect "${page.page_name}"? Leads from this page will stop arriving in the EMS.`)) return;
    setBusy(true);
    try {
      await disconnectMetaPage(page.page_id);
      await load();
      toast('Page disconnected.');
    } catch (e) {
      toast(e.response?.data?.error || 'Could not disconnect that page.');
    } finally {
      setBusy(false);
    }
  };

  const pages = status?.pages || [];

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="settings-section-title">Facebook Lead Ads</div>

      {loading ? (
        <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>Loading…</p>
      ) : unavailable ? (
        <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>{unavailable}</p>
      ) : !status.app_configured ? (
        <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>
          Facebook connection isn’t enabled on this server yet. Please contact support.
        </p>
      ) : (
        <>
          <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 14 }}>
            {pages.length
              ? 'Leads from these Facebook pages are captured automatically.'
              : 'Connect your Facebook page and every Instant Form lead will land here automatically. No setup, no tokens — just sign in with Facebook.'}
          </p>

          {pages.map(p => (
            <div key={p.page_id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
              borderBottom: '0.5px solid var(--gray-100)', flexWrap: 'wrap',
            }}>
              <i className="ti ti-brand-facebook" style={{ fontSize: 18, color: '#1877F2' }} />
              <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{p.page_name}</div>
                <div style={{ fontSize: 11, color: p.needs_reconnect ? 'var(--red)' : 'var(--gray-400)' }}>
                  {p.needs_reconnect
                    ? 'Reconnect needed — Facebook rejected the saved permission'
                    : p.last_lead_at
                      ? `Last lead ${relTime(p.last_lead_at)}`
                      : 'Connected — waiting for the first lead'}
                </div>
              </div>
              {p.needs_reconnect
                ? <span className="status-badge" style={{ background: '#dc262622', color: 'var(--red)' }}>Action needed</span>
                : <span className="status-badge" style={{ background: '#05966922', color: '#059669' }}>Active</span>}
              {canManage && (
                <button className="btn btn-outline btn-sm" onClick={() => disconnect(p)} disabled={busy}
                        style={{ color: 'var(--red)' }}>
                  Disconnect
                </button>
              )}
            </div>
          ))}

          {canManage && (
            <button
              className="btn btn-primary"
              onClick={connect}
              disabled={busy}
              style={{ marginTop: pages.length ? 14 : 0, background: '#1877F2', borderColor: '#1877F2',
                       display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <i className="ti ti-brand-facebook" style={{ fontSize: 16 }} />
              {busy ? 'Waiting for Facebook…' : pages.length ? 'Connect another page' : 'Connect Facebook'}
            </button>
          )}
        </>
      )}

      <Modal open={!!picker} onClose={() => !saving && setPicker(null)} title="Choose your pages">
        <p style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 12 }}>
          Pick the page(s) whose lead form submissions should come into the EMS.
        </p>
        <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 16 }}>
          {picker?.pages.map(p => {
            // Blocked = already another customer's page, or this user lacks the
            // lead permission on it, in which case connecting would look
            // successful but never deliver anything.
            const blocked = p.taken_by_other;
            return (
              <label key={p.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 4px',
                borderBottom: '0.5px solid var(--gray-100)',
                opacity: blocked ? 0.5 : 1, cursor: blocked ? 'not-allowed' : 'pointer',
              }}>
                <input
                  type="checkbox"
                  disabled={blocked}
                  checked={!!selected[p.id]}
                  onChange={e => setSelected(s => ({ ...s, [p.id]: e.target.checked }))}
                />
                {p.picture
                  ? <img src={p.picture} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
                  : <i className="ti ti-brand-facebook" style={{ fontSize: 20, color: '#1877F2' }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</div>
                  {blocked ? (
                    <div style={{ fontSize: 11, color: 'var(--red)' }}>Already connected to another account</div>
                  ) : !p.can_manage_leads ? (
                    <div style={{ fontSize: 11, color: '#b45309' }}>
                      You may not have lead access on this page — ask a page admin to grant it
                    </div>
                  ) : p.already_connected ? (
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Already connected</div>
                  ) : null}
                </div>
              </label>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-outline" onClick={() => setPicker(null)} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={savePages} disabled={saving}>
            {saving ? 'Connecting…' : 'Connect selected'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
